import Alert from '@mui/material/Alert';
import { listLessonVerifications } from '@platform/client';
import { useQuery } from '@tanstack/react-query';

import type { AlertColor } from '@mui/material/Alert';
import type { ListLessonVerificationsResponse } from '@platform/client';

/**
 * Whether the lesson as it is now is Verified — CURR-28's answer, on the lesson page for
 * CURR-33. An applied fix changes the lesson, so the run that verified it stops counting,
 * and this is where the admin sees that.
 */
export function verifiedLine({
  verified,
  runs,
}: ListLessonVerificationsResponse): { severity: AlertColor; message: string } {
  if (verified !== undefined)
    return { severity: 'success', message: 'Verified against its sources.' };

  if (runs.some((run) => run.passed && !run.current))
    return {
      severity: 'warning',
      message: 'Changed since it was verified. It needs verifying again.',
    };

  if (runs.some((run) => run.current))
    return {
      severity: 'warning',
      message: 'The last verification did not pass.',
    };

  return { severity: 'info', message: 'Not verified yet.' };
}

/** Keyed under `review` so every refresh of the lesson page reads it again. */
export function Verified({ lessonId }: { lessonId: string }) {
  const report = useQuery({
    queryKey: ['review', lessonId, 'verifications'],
    queryFn: () =>
      listLessonVerifications({ path: { id: lessonId }, throwOnError: true }),
  });

  const data = report.data?.data;
  if (data === undefined) return null;

  const line = verifiedLine(data);

  return (
    <Alert severity={line.severity} data-testid="verified">
      {line.message}
    </Alert>
  );
}
